import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  MtLitterGetBreederBreedsQuery,
  MtLitterGetBreederBreedsQueryVariables,
} from '@gql/graphql';
import type { SelectOption } from '@presentation/components';
import { sortBy } from 'lodash/fp';
import { useMemo } from 'react';

export const MT_LITTER_GET_BREEDER_BREEDS = graphql(`
  query MtLitterGetBreederBreeds($translationsWhere: DogBreedTranslationWhereInput) {
    accountMe {
      id
      dogBreeder {
        id
        breederBreeds {
          id
          dogBreed {
            id
            name
            sizeCategory
            baseBreedName
            translations(where: $translationsWhere) {
              id
              name
              otherNames
            }
          }
        }
      }
    }
  }
`);

export const useGetBreederBreeds = (options?: { skip: boolean }) => {
  const { skip = false } = options ?? {};
  const { language } = useRouter();

  const variables = useMemo((): MtLitterGetBreederBreedsQueryVariables => {
    return {
      translationsWhere: { language: { equals: language } },
    };
  }, [language]);

  const { data, loading, error } = useQuery<
    MtLitterGetBreederBreedsQuery,
    MtLitterGetBreederBreedsQueryVariables
  >(MT_LITTER_GET_BREEDER_BREEDS, {
    variables,
    skip,
  });

  const breedOptions: SelectOption<number>[] = useMemo(() => {
    const sortedByName = sortBy(
      (b) => b.dogBreed?.translations?.[0]?.name?.toLocaleLowerCase(),
      data?.accountMe?.dogBreeder?.breederBreeds ?? [],
    );

    return sortedByName.map(
      (breed) =>
        ({
          value: breed.dogBreed.id,
          label: breed.dogBreed.translations[0].name,
          searchValues: breed.dogBreed.translations[0].otherNames,
        } as SelectOption<number>),
    );
  }, [data?.accountMe]);

  return {
    breedsLoading: loading,
    breedsError: error,
    breedOptions,
  };
};
